import { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';
import { useAuthStore, useOrganizationStore } from '@/store';
import { OrganizationService } from '@/services';
import { CaseManager, CaseManagerModal, CaseManagerPayload, CaseManagerState, CaseManagerTable } from '@/types';

export default defineStore('CaseManagerStore', {
  state: (): CaseManagerState => ({
    caseManagers: [],
    error: '',
    loading: false,
    showArchived: false,
  }),

  getters: {
    orgId: () => (useOrganizationStore().organization.id as number) || useAuthStore().organizationId,
  },

  actions: {
    catchError(e: string) {
      this.loading = false;
      this.error = 'error';
      throw e;
    },

    async attachCaseManagers(caseManagers: number[]): Promise<AxiosResponse<unknown>> {
      const organizationId = this.orgId;
      this.loading = true;
      const payload = { case_managers: caseManagers } as CaseManagerPayload;
      return await OrganizationService.attachPerson(payload, organizationId, 'case-managers')
        .then((result) => {
          this.loading = false;
          return result;
        })
        .catch(this.catchError);
    },

    async detachCaseManagers(caseManagers: number[]): Promise<AxiosResponse<unknown>> {
      const organizationId = this.orgId;
      const payload = { case_managers: caseManagers } as CaseManagerPayload;
      return await OrganizationService.detachPerson(payload, organizationId, 'case-managers')
        .then((result) => {
          this.loading = false;
          return result;
        })
        .catch(this.catchError);
    },

    async getCaseManagers(filter: string): Promise<CaseManager[]> {
      const organizationId = this.orgId;
      this.loading = true;
      return await OrganizationService.getPerson(organizationId, 'case-managers', filter)
        .then((result) => {
          this.loading = false;
          this.caseManagers = result.data?.data;
          return this.caseManagers;
        })
        .catch(this.catchError);
    },

    async getCaseManagersForTable(filter: string): Promise<CaseManagerTable[]> {
      await this.getCaseManagers(filter);
      const tableData = this.caseManagers.map((caseManager) => {
        return {
          id: caseManager.id,
          name: caseManager.display_name as string,
          archived: !!caseManager.archived_at,
        };
      });
      return this.showArchived ? tableData : tableData.filter((item) => !item.archived);
    },

    async getCaseManagersAvailableToAdd(): Promise<CaseManagerModal[]> {
      const organizationId = this.orgId;
      this.loading = true;
      return await OrganizationService.getPersonAvailableToAdd(organizationId, 'case-managers')
        .then((result) => {
          this.loading = false;
          return result.data?.data
            .filter((item) => !item.archived_at)
            .map((caseManager) => {
              return {
                value: caseManager.id,
                name: caseManager.display_name as string,
              };
            });
        })
        .catch(this.catchError);
    },
  },
});
